import { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, SelectInput } from '@/components/atoms/Input';
import { requestDesbloqueioConfiancaHubsoft } from '../services/desbloqueioHubsoft.api';
import { useConexoes } from './ConexoesSection';

const schema = z.object({
  codconexao: z.string().min(1, 'Selecione a conexão'),
  qtd_dias: z.coerce.number().min(1, 'Informe a quantidade de dias (mínimo 1)'),
});

type FormData = z.infer<typeof schema>;

interface DesbloqueioMkModalProps {
  isOpen: boolean;
  onClose: () => void;
  colors: { primaryColor: string };
}

export function DesbloqueioMkModal({ isOpen, onClose, colors }: DesbloqueioMkModalProps) {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { conexoes } = useConexoes(isOpen);
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { codconexao: '', qtd_dias: 1 },
  });

  useEffect(() => {
    if (isOpen) form.reset({ codconexao: '', qtd_dias: 1 });
  }, [isOpen]);

  const submit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await requestDesbloqueioConfiancaHubsoft(Number(data.codconexao), data.qtd_dias);
      toast({
        title: 'Confiança desbloqueada com sucesso, aguarde o contato do nosso atendimento',
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
      onClose();
    } catch (requestError: unknown) {
      const message = (requestError as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        title: message || 'Erro ao desbloquear confiança',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!isSubmitting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Desbloqueio de Confiança</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        <form onSubmit={form.handleSubmit(submit)}>
          <ModalBody>
            <VStack spacing={4}>
              <SelectInput
                control={form.control}
                name="codconexao"
                label="Conexão"
                placeholder="Selecione a conexão"
                options={conexoes.map((conexao) => ({
                  value: String(conexao.codconexao),
                  label: `Código ${conexao.codconexao} — ${conexao.endereco}`,
                }))}
              />
              <Input
                control={form.control}
                name="qtd_dias"
                label="Quantidade de dias"
                type="number"
                min={1}
                placeholder="Informe a quantidade de dias"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              backgroundColor="transparent"
              color={colors.primaryColor}
              onClick={onClose}
              marginRight={2}
              isDisabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} isDisabled={conexoes.length === 0}>
              Confirmar
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
